import base_url, { gcb_base_url } from "./baseUrl";
import { getUserToken, logoutUser } from ".";
import { config } from "./config";
import { refreshAuthToken } from "./authTokens";

// GCB member document download (returns blob or JSON)
export const downloadGcbDocument = async (type, ssnitNumber) => {
  const doFetch = () =>
    fetch(gcb_base_url + 'gcb-member-document/download', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getUserToken()}`,
        'Accept': 'application/json, application/pdf',
      },
      body: JSON.stringify({ type, ssnit_number: ssnitNumber }),
    });

  let response = await doFetch();

  if (response.status === 401) {
    try {
      await refreshAuthToken();
      response = await doFetch();
    } catch (e) {
      logoutUser();
      throw e;
    }
  }

  if (!response.ok) {
    if (response.status === 401) logoutUser();
    let errMsg = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errJson = await response.json();
      errMsg = errJson.message || errMsg;
    } catch (e) {
      // not JSON error
    }
    throw new Error(errMsg);
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const json = await response.json();

    // GCB wraps base64 PDF in JSON: { success: true, data: "<base64>", filename: "..." }
    if (json && json.success === true && typeof json.data === 'string') {
      try {
        const cleanBase64 = json.data.replace(/\s/g, '');
        const byteCharacters = atob(cleanBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const filename = json.filename || `${type}_${ssnitNumber}.pdf`;
        return { blob, filename, isBlob: true };
      } catch (decodeErr) {
        throw new Error('Failed to decode base64 PDF from GCB response');
      }
    }

    // If JSON but no base64 data, return as-is (or throw if it indicates failure)
    return json;
  }

  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') || '';
  const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  const filename = match && match[1] ? match[1].replace(/['"]/g, '') : `${type}_${ssnitNumber}.pdf`;

  return { blob, filename, isBlob: true };
};

// ✅ Main serverRequest (JSON only, with timeout)
export let serverRequest = (type, param, data, otpRequired = false, otp = null) => {
  let URL = base_url;
  let requestCompleted = false;
  let didRetryAfterRefresh = false;

  return new Promise((resolve, reject) => {
    // Add timeout to prevent infinite loading - using config timeout
    const timeoutId = setTimeout(() => {
      if (!requestCompleted) {
        requestCompleted = true;
  // Only reject, let consumer handle notification
        reject(new Error("Request timeout"));
      }
    }, config.api.timeout);

    const doFetch = () =>
      fetch(URL + param, {
        method: type,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getUserToken()}`,
          "Accept": "application/json",
          "otp": otpRequired ? otp : null,
        },
        body: JSON.stringify(data),
      });

    doFetch()
      .then(async (response) => {
        if (requestCompleted) return;
        clearTimeout(timeoutId);
        let resp;
        try {
          resp = await response.json();
        } catch (e) {
          resp = null;
        }
        if (!response.ok) {
          if (response.status === 401) {
            // Attempt token refresh once, then retry the original request.
            if (!didRetryAfterRefresh) {
              didRetryAfterRefresh = true;
              try {
                await refreshAuthToken();
                return await doFetch().then(async (retryResponse) => {
                  let retryJson = null;
                  try {
                    retryJson = await retryResponse.json();
                  } catch (e) {
                    retryJson = null;
                  }
                  if (!retryResponse.ok) {
                    const retryErr = new Error(
                      retryJson && retryJson.message
                        ? retryJson.message
                        : `HTTP ${retryResponse.status}: ${retryResponse.statusText}`
                    );
                    if (retryJson) retryErr.apiResponse = retryJson;
                    throw retryErr;
                  }
                  return retryJson;
                });
              } catch (e) {
                logoutUser();
                throw e;
              }
            }
            logoutUser();
          }
          // Prefer backend message if present and attach full response for downstream handling
          const err = new Error(resp && resp.message ? resp.message : `HTTP ${response.status}: ${response.statusText}`);
          if (resp) err.apiResponse = resp;
          throw err;
        }
        return resp;
      })
      .then((resp) => {
        if (requestCompleted) return;
        requestCompleted = true;
        if (resp && resp.success) {
          resolve(resp);
        } else {
          // Reject with an Error that carries the full API response so UI can present detailed errors
          const err = new Error((resp && resp.message) ? resp.message : 'Request failed');
          if (resp) err.apiResponse = resp;
          reject(err);
        }
      })
      .catch((error) => {
        if (requestCompleted) return;
        requestCompleted = true;
        clearTimeout(timeoutId);
        reject(error);
      })
      .finally(() => {
        if (!requestCompleted) {
          requestCompleted = true;
          clearTimeout(timeoutId);
        }
      });
  });
};

// ✅ NEW: FormData request handler (file upload only)
export const serverFormRequest = (type, param, formData, otpRequired = false, otp = null) => {
  let URL = base_url;
  let requestCompleted = false;
  let didRetryAfterRefresh = false;

  return new Promise((resolve, reject) => {
    const doFetch = () =>
      fetch(URL + param, {
        method: type,
        headers: {
          "Authorization": `Bearer ${getUserToken()}`,
          "Accept": "application/json",
          ...(otpRequired ? { otp } : {})
          // ⚠️ No Content-Type, browser handles it for FormData
        },
        body: formData,
      });

    doFetch()
      .then(async (response) => {
        if (requestCompleted) return;
        let resp;
        try {
          resp = await response.json();
        } catch (e) {
          resp = null;
        }
        if (!response.ok) {
          if (response.status === 401) {
            if (!didRetryAfterRefresh) {
              didRetryAfterRefresh = true;
              try {
                await refreshAuthToken();
                return await doFetch().then(async (retryResponse) => {
                  let retryJson = null;
                  try {
                    retryJson = await retryResponse.json();
                  } catch (e) {
                    retryJson = null;
                  }
                  if (!retryResponse.ok) {
                    const retryErr = new Error(
                      retryJson && retryJson.message
                        ? retryJson.message
                        : `HTTP ${retryResponse.status}: ${retryResponse.statusText}`
                    );
                    if (retryJson) retryErr.apiResponse = retryJson;
                    throw retryErr;
                  }
                  return retryJson;
                });
              } catch (e) {
                logoutUser();
                throw e;
              }
            }
            logoutUser();
          }
          const err = new Error(resp && resp.message ? resp.message : `HTTP ${response.status}: ${response.statusText}`);
          if (resp) err.apiResponse = resp;
          throw err;
        }
        return resp;
      })
      .then((resp) => {
        if (requestCompleted) return;
        requestCompleted = true;
        if (resp && resp.success) {
          resolve(resp);
        } else {
          const err = new Error((resp && resp.message) ? resp.message : 'Request failed');
          if (resp) err.apiResponse = resp;
          reject(err);
        }
      })
      .catch((error) => {
        if (requestCompleted) return;
        requestCompleted = true;
        reject(error);
      });
  });
};

// ✅ Unauthenticated JSON request handler (fixed)
export let serverRequestNoBearer = (type, param, data) => {
  let URL = base_url;
  let requestCompleted = false;

  return new Promise((resolve, reject) => {
    fetch(URL + param, {
      method: type,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        // No Authorization header for unauthenticated requests
      },
      body: JSON.stringify(data),
    })
      .then(async (response) => {
        if (requestCompleted) return;
        let resp;
        try {
          resp = await response.json();
        } catch (e) {
          resp = null;
        }
        if (!response.ok) {
          const err = new Error(resp && resp.message ? resp.message : `HTTP ${response.status}: ${response.statusText}`);
          if (resp) err.apiResponse = resp;
          throw err;
        }
        return resp;
      })
      .then((resp) => {
        if (requestCompleted) return;
        requestCompleted = true;
        if (resp && resp.success) {
          resolve(resp);
        } else {
          const err = new Error((resp && resp.message) ? resp.message : 'Request failed');
          if (resp) err.apiResponse = resp;
          reject(err);
        }
      })
      .catch((error) => {
        if (requestCompleted) return;
        requestCompleted = true;
        reject(error);
      })
      .finally(() => {
        if (!requestCompleted) requestCompleted = true;
      });
  });
};
